/**
 * Pre-filled external reference & fraud-check search URLs for a vendor.
 * Everything opens in a new tab — we never scrape or host third-party data.
 *
 * Every source below is a search URL parameterised with the vendor name, so
 * clicking Better Business Bureau (say) drops the founder straight into that
 * site's own results for the vendor. If the admin later attaches an explicit
 * URL on the vendor doc's `external_refs` array, we prefer that one.
 */

const q = (s) => encodeURIComponent(String(s || "").trim());

export const EXTERNAL_REF_SOURCES = [
  {
    key: "bbb",
    label: "Better Business Bureau",
    short: "BBB",
    kind: "reputation",
    tone: "sky",
    describe: "Complaint history + accreditation status",
    urlFor: (name) => `https://www.bbb.org/search?find_text=${q(name)}`,
  },
  {
    key: "google",
    label: "Google Reviews",
    short: "Google",
    kind: "reviews",
    tone: "sky",
    describe: "Star rating + open review threads",
    urlFor: (name) => `https://www.google.com/search?q=${q(name + " reviews")}`,
  },
  {
    key: "trustpilot",
    label: "Trustpilot",
    short: "Trustpilot",
    kind: "reviews",
    tone: "sky",
    describe: "Verified customer reviews",
    urlFor: (name) => `https://www.trustpilot.com/search?query=${q(name)}`,
  },
  {
    key: "linkedin",
    label: "LinkedIn Company",
    short: "LinkedIn",
    kind: "profile",
    tone: "sky",
    describe: "Company page + leadership footprint",
    urlFor: (name) => `https://www.linkedin.com/search/results/companies/?keywords=${q(name)}`,
  },
  {
    key: "fda",
    label: "FDA Warning Letters",
    short: "FDA",
    kind: "fraud",
    tone: "red",
    describe: "Any FDA compliance actions on record",
    urlFor: (name) => `https://www.fda.gov/inspections-compliance-enforcement-and-criminal-investigations/compliance-actions-and-activities/warning-letters?search_api_fulltext=${q(name)}`,
  },
  {
    key: "usda-recalls",
    label: "USDA Recalls",
    short: "USDA",
    kind: "fraud",
    tone: "red",
    describe: "Recalls tied to this facility / brand",
    urlFor: (name) => `https://www.fsis.usda.gov/recalls-alerts/search?search_api_fulltext=${q(name)}`,
  },
  {
    key: "ftc",
    label: "FTC Actions",
    short: "FTC",
    kind: "fraud",
    tone: "red",
    describe: "Federal Trade Commission enforcement history",
    urlFor: (name) => `https://www.ftc.gov/search?query=${q(name)}`,
  },
];

/**
 * Merge the seven default search-link sources with any admin-entered
 * per-vendor overrides. Overrides can add `url`, `rating`, and `note`.
 */
export function buildVendorRefs(vendor) {
  const name = vendor?.name || "";
  const overrides = Array.isArray(vendor?.external_refs) ? vendor.external_refs : [];
  const byKey = new Map();
  overrides.forEach((o) => {
    if (o?.key) byKey.set(o.key, o);
  });
  return EXTERNAL_REF_SOURCES.map((src) => {
    const o = byKey.get(src.key) || {};
    return {
      ...src,
      url: o.url || src.urlFor(name),
      rating: typeof o.rating === "number" ? o.rating : null,
      note: o.note || "",
      isCustom: !!o.url || typeof o.rating === "number",
    };
  });
}
